import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { getProjects, getTasks, createProject, updateProject, deleteProject, getDepartments, classifyProject, reorderItems, getMyCompanies } from '../api';
import { deleteTask as apiDeleteTask } from '../api';
import api from '../api';
import ProjectNotesPanel from '../components/ProjectNotesPanel';
import ProjectDetailModal from '../components/ProjectDetailModal';
import { IconAIAgent } from '../components/Icons';
import QuickStartWizard from '../components/QuickStartWizard';
import TaskDetailModal from '../components/TaskDetailModal';
import { getScheduleHealth, HEALTH, isDueSoon } from '../utils/scheduleHealth';
import UserSelector from '../components/UserSelector';
import { usePermissions } from '../context/PermissionsContext';
import { decodeEntities } from '../utils/decodeEntities';

if (typeof document !== 'undefined' && !document.getElementById('gpf-urgency-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'gpf-urgency-pulse-style';
  style.textContent = `
    @keyframes gpf-pulse-red   { 0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0);   } 50% { box-shadow: 0 0 0 4px rgba(220,38,38,0.28); } }
    @keyframes gpf-pulse-amber { 0%, 100% { box-shadow: 0 0 0 0 rgba(217,119,6,0);   } 50% { box-shadow: 0 0 0 4px rgba(217,119,6,0.24); } }
    .gpf-urgent-overdue  { animation: gpf-pulse-red 2.4s ease-in-out infinite; }
    .gpf-urgent-duesoon  { animation: gpf-pulse-amber 2.4s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .gpf-urgent-overdue, .gpf-urgent-duesoon { animation: none; }
    }
  `;
  document.head.appendChild(style);
}


const PRIORITIES = ['low', 'medium', 'high'];
const STATUSES   = ['not_started', 'active', 'completed', 'archived'];

const emptyForm = {
  title: '', description: '', status: 'active',
  priority: 'medium', progress: 0, start_date: '', end_date: '',
  assigned_users: [], department: '', company_id: '',
};

// ── ProjectForm defined OUTSIDE component to prevent remounting on each render ──
const ProjectForm = ({ data, onChange, departments, companies, classifying, onDescriptionBlur, onSubmit, onCancel, saving, isEdit }) => (
  <form onSubmit={onSubmit} style={styles.form}>
    <div style={styles.formGrid}>
      <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
        <label style={styles.label}>Project Name *</label>
        <input style={styles.input} value={data.title} onChange={e => onChange({ ...data, title: e.target.value })} placeholder="Enter project name" required />
      </div>
      {companies?.length > 1 && (
        <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Company *</label>
          <select style={styles.input} value={data.company_id} onChange={e => onChange({ ...data, company_id: e.target.value })}>
            <option value="">Select company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
        <label style={styles.label}>Description</label>
        <textarea
          style={{ ...styles.input, height: 70, resize: 'vertical' }}
          value={data.description}
          onChange={e => onChange({ ...data, description: e.target.value })}
          onBlur={onDescriptionBlur}
          placeholder="What is this project about?"
        />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Status</label>
        <select style={styles.input} value={data.status} onChange={e => onChange({ ...data, status: e.target.value })}>
          {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Priority</label>
        <select style={styles.input} value={data.priority} onChange={e => onChange({ ...data, priority: e.target.value })}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Start Date</label>
        <input style={styles.input} type="date" value={data.start_date} onChange={e => onChange({ ...data, start_date: e.target.value })} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>End Date</label>
        <input style={styles.input} type="date" value={data.end_date} onChange={e => onChange({ ...data, end_date: e.target.value })} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Progress (auto-calculated)</label>
        <div style={{ ...styles.input, background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${data.progress || 0}%`, background: '#2563eb', borderRadius: 99 }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', minWidth: 36 }}>{data.progress || 0}%</span>
        </div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>
          Department
          {classifying && <span style={{ marginLeft: 8, fontSize: 11, color: '#2563eb' }}>🤖 AI classifying...</span>}
        </label>
        <select style={styles.input} value={data.department} onChange={e => onChange({ ...data, department: e.target.value })}>
          <option value="">Select department...</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
        <label style={styles.label}>Assigned Users</label>
        <UserSelector selected={data.assigned_users || []} onChange={val => onChange({ ...data, assigned_users: val })} />
      </div>
    </div>
    <div style={styles.formActions}>
      <button type="button" style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
      <button type="submit" style={styles.primaryBtn} disabled={saving}>{saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Project')}</button>
    </div>
  </form>
);

export default function ProjectsPage() {
  const { can } = usePermissions();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [projects,    setProjects]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [showAiUpgrade,  setShowAiUpgrade]  = useState(false);

  const canUseAiAgent = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      const role = stored?.role || '';
      if (role === 'administrator') return true; // platform superadmin bypass
      const cached = JSON.parse(localStorage.getItem('gpf_plan_features') || '{}');
      const workspace = JSON.parse(localStorage.getItem('gpf_workspace_cache') || '{}');
      const plan = workspace?.plan || 'free';
      return cached?.[plan]?.ai_agent === true;
    } catch { return false; }
  };

  const handleQuickStartClick = () => {
    if (!canUseAiAgent()) { setShowAiUpgrade(true); return; }
    setShowQuickStart(true);
  };
  const [form,        setForm]        = useState(emptyForm);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const [filter,        setFilter]        = useState('all');
  const [healthFilter,  setHealthFilter]  = useState(null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [dragProjectId, setDragProjectId] = useState(null);
  const [dragTaskInfo,  setDragTaskInfo]  = useState(null); // { projectId, taskId }

  const canReorderProjects = filter === 'all' && !healthFilter;

  const toggleExpanded = (id) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleCompanyExpanded = (id) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleProjectDrop = async (targetId) => {
    if (!dragProjectId || dragProjectId === targetId) { setDragProjectId(null); return; }
    const current = [...projects];
    const fromIdx = current.findIndex(p => p.id === dragProjectId);
    const toIdx   = current.findIndex(p => p.id === targetId);
    if (fromIdx === -1 || toIdx === -1) { setDragProjectId(null); return; }
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    setProjects(current); // optimistic reorder — instant visual feedback
    setDragProjectId(null);
    try {
      await reorderItems('gpf_project', current.map(p => p.id));
    } catch (e) { console.error('Failed to save project order:', e); fetchProjects(); }
  };

  const handleTaskDrop = async (projectId, targetTaskId) => {
    if (!dragTaskInfo || dragTaskInfo.projectId !== projectId || dragTaskInfo.taskId === targetTaskId) {
      setDragTaskInfo(null); return;
    }
    const current = [...(taskMap[projectId] || [])];
    const fromIdx = current.findIndex(t => t.id === dragTaskInfo.taskId);
    const toIdx   = current.findIndex(t => t.id === targetTaskId);
    if (fromIdx === -1 || toIdx === -1) { setDragTaskInfo(null); return; }
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    setTaskMap(prev => ({ ...prev, [projectId]: current })); // optimistic
    setDragTaskInfo(null);
    try {
      await reorderItems('gpf_task', current.map(t => t.id));
    } catch (e) { console.error('Failed to save task order:', e); fetchProjects(); }
  };
  const [showUpgrade,   setShowUpgrade]   = useState(false);
  const [upgradeMsg,    setUpgradeMsg]    = useState('');
  const [notesProject,  setNotesProject]  = useState(null);
  const [detailProject, setDetailProject] = useState(null);
  const [detailTask,    setDetailTask]    = useState(null);
  const [attachCounts,  setAttachCounts]  = useState({});
  const [editProject, setEditProject] = useState(null);
  const [editForm,    setEditForm]    = useState(emptyForm);
  const [taskMap,     setTaskMap]     = useState({});
  const [departments, setDepartments] = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [classifying, setClassifying] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await getProjects();
      const data = res.data;
      setProjects(data);

      // Load ALL tasks upfront and group by project (no manual expand needed)
      if (data.length > 0) {
        try {
          const taskRes = await api.get('/wp/v2/tasks?per_page=100');
          const allTasks = taskRes.data || [];
          const counts = {};
          const grouped = {};
          data.forEach(p => { grouped[p.id] = []; });
          allTasks.forEach(task => {
            const projId = parseInt(task.meta?.gpf_project_id);
            if (!projId || !grouped[projId]) return;
            grouped[projId].push(task);
            try {
              const fileIds = JSON.parse(task.meta?.gpf_file_ids || '[]');
              counts[projId] = (counts[projId] || 0) + fileIds.length;
            } catch {}
          });
          setAttachCounts(counts);
          setTaskMap(grouped);
          setExpandedProjects(new Set(data.filter(p => (grouped[p.id] || []).length > 0).map(p => p.id))); // only expand projects that have tasks
        } catch(e) { console.error(e); }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchProjects();
    getDepartments().then(res => setDepartments(res.data)).catch(console.error);
    getMyCompanies().then(res => {
      const list = res.data || [];
      setCompanies(list);
      if (list.length === 1) {
        setForm(f => ({ ...f, company_id: list[0].id }));
      }
    }).catch(console.error);
  }, []);

  // Runs whenever either companies or projects finish loading (they fetch
  // independently), so a company is only auto-expanded once we actually know
  // whether it has any projects to show.
  useEffect(() => {
    if (companies.length === 0 || projects.length === 0) return;
    const withProjects = companies.filter(c => projects.some(p => String(p.meta?.gpf_company_id) === String(c.id)));
    setExpandedCompanies(new Set(withProjects.map(c => c.id)));
  }, [companies, projects]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') setShowForm(true);
  }, [location.search]);

  const handleDescriptionBlur = useCallback(async (currentForm, setFormFn) => {
    if (!currentForm.title) return;
    setClassifying(true);
    setError('');
    try {
      const res = await classifyProject(currentForm.title, currentForm.description);
      if (res.data?.department) {
        setFormFn(prev => ({ ...prev, department: res.data.department }));
      } else {
        setError('AI could not determine a department — please select one manually.');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Unknown error';
      setError(`AI department suggestion unavailable: ${msg}`);
      console.error('classifyProject failed:', e);
    }
    finally { setClassifying(false); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.start_date) { setError('Start date is required.'); return; }
    if (!form.end_date)   { setError('End date is required.'); return; }
    if (form.end_date < form.start_date) { setError('End date must be after the start date.'); return; }
    if (!form.assigned_users?.length) { setError('Please assign at least one user.'); return; }
    setSaving(true);
    try {
      const res = await createProject(form);
      setForm(emptyForm);
      setShowForm(false);
      // Optimistic append — avoids an expensive full reload of every project's tasks
      setProjects(prev => [res.data, ...prev]);
      setTaskMap(prev => ({ ...prev, [res.data.id]: [] }));
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create project.';
      if (err.response?.status === 403 && msg.includes('plan')) {
        setShowForm(false);
        setUpgradeMsg(msg);
        setShowUpgrade(true);
      } else {
        setError(msg);
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis will permanently delete the project and all its tasks. Once deleted, it's gone forever and cannot be recovered.`)) return;
    try {
      await deleteProject(id);
      setProjects(projects.filter(p => p.id !== id));
    } catch (err) { alert('Failed to delete project.'); }
  };

  const handleDeleteTask = async (taskId, title, projectId) => {
    if (!window.confirm(`Delete task "${title}"?\n\nOnce deleted, it's gone forever and cannot be recovered.`)) return;
    try {
      await apiDeleteTask(taskId);
      setTaskMap(prev => ({ ...prev, [projectId]: (prev[projectId] || []).filter(t => t.id !== taskId) }));
    } catch (err) { alert('Failed to delete task.'); }
  };

  const handleEdit = (project) => {
    setEditProject(project);
    setEditForm({
      title:          decodeEntities(project.title?.rendered) || '',
      description:    decodeEntities(project.content?.rendered)?.replace(/<[^>]+>/g, '') || '',
      status:         project.meta?.gpf_status     || 'active',
      priority:       project.meta?.gpf_priority   || 'medium',
      progress:       project.meta?.gpf_progress   || 0,
      start_date:     project.meta?.gpf_start_date || '',
      end_date:       project.meta?.gpf_end_date   || '',
      assigned_users: JSON.parse(project.meta?.gpf_assigned_users || '[]'),
      department:     project.meta?.gpf_department || '',
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const wasStarted  = editProject.meta?.gpf_started_by;
      const isStarting  = editForm.status === 'active' && !wasStarted;
      const currentUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      const updateData  = { ...editForm };
      if (isStarting) {
        updateData.started_by   = currentUser.name || 'Unknown';
        updateData.started_date = new Date().toISOString().split('T')[0];
      }
      await updateProject(editProject.id, updateData);
      // Optimistic local update — instant UI, no full project list reload
      setProjects(prev => prev.map(p => p.id !== editProject.id ? p : {
        ...p,
        title:   { rendered: editForm.title },
        content: { rendered: editForm.description },
        meta: {
          ...p.meta,
          gpf_status:         editForm.status,
          gpf_priority:       editForm.priority,
          gpf_progress:       editForm.progress,
          gpf_start_date:     editForm.start_date,
          gpf_end_date:       editForm.end_date,
          gpf_assigned_users: JSON.stringify(editForm.assigned_users || []),
          gpf_department:     editForm.department,
          gpf_started_by:     isStarting ? (currentUser.name || 'Unknown') : (p.meta?.gpf_started_by || ''),
          gpf_started_date:   isStarting ? updateData.started_date : (p.meta?.gpf_started_date || ''),
        },
      }));
      setEditProject(null);
    } catch (err) { console.error(err); fetchProjects(); }
    finally { setSaving(false); }
  };

  const handleDuplicate = async (project) => {
    try {
      const res = await createProject({
        title:          'COPY — ' + (decodeEntities(project.title?.rendered) || 'Untitled'),
        description:    decodeEntities(project.content?.rendered)?.replace(/<[^>]+>/g, '') || '',
        status:         'active', priority: project.meta?.gpf_priority || 'medium',
        progress: 0, start_date: project.meta?.gpf_start_date || '',
        end_date: project.meta?.gpf_end_date || '',
        assigned_users: JSON.parse(project.meta?.gpf_assigned_users || '[]'),
        department: project.meta?.gpf_department || '',
      });
      setProjects(prev => [res.data, ...prev]);
      setTaskMap(prev => ({ ...prev, [res.data.id]: [] }));
    } catch (err) { console.error(err); }
  };

  const today    = new Date().toISOString().split('T')[0];
  const getAttachmentCount = (project) => {
    try {
      const ids = project.meta?.gpf_file_ids;
      if (!ids) return 0;
      const parsed = typeof ids === 'string' ? JSON.parse(ids) : ids;
      return Array.isArray(parsed) ? parsed.length : 0;
    }
    catch { return 0; }
  };

  // Overdue takes priority over not_started/active so a project only appears in ONE tab
  const getEffectiveStatus = (p) => {
    const status = p.meta?.gpf_status || 'not_started';
    const endDate = p.meta?.gpf_end_date;
    if (status !== 'completed' && endDate && endDate < today) return 'overdue';
    return status;
  };

  const filtered = projects.filter(p => {
    const effective = getEffectiveStatus(p);
    let matchesTab = true;
    if (filter === 'not_started') matchesTab = effective === 'not_started';
    else if (filter === 'active')    matchesTab = effective === 'active';
    else if (filter === 'overdue')   matchesTab = effective === 'overdue';
    else if (filter === 'completed') matchesTab = effective === 'completed';
    if (!matchesTab) return false;

    if (healthFilter) {
      const startDate = p.meta?.gpf_start_date || '';
      const endDate   = p.meta?.gpf_end_date   || '';
      const progress  = parseInt(p.meta?.gpf_progress || 0);
      const status    = p.meta?.gpf_status || 'not_started';
      const health    = getScheduleHealth(startDate, endDate, progress, status);
      if (health.label !== healthFilter) return false;
    }
    return true;
  });

  const statusLabel   = (s) => ({ not_started: 'Not Started', active: 'Active', overdue: 'Overdue', completed: 'Completed', archived: 'Archived', all: 'All' }[s] || s);
  const statusStyle   = (s) => ({ active: { bg: '#eff6ff', text: '#2563eb' }, completed: { bg: '#f0fdf4', text: '#16a34a' }, archived: { bg: '#f8fafc', text: '#64748b' } }[s] || { bg: '#eff6ff', text: '#2563eb' });
  const priorityStyle = (p) => ({ high: { bg: '#fef2f2', text: '#dc2626' }, medium: { bg: '#fffbeb', text: '#d97706' }, low: { bg: '#f0fdf4', text: '#16a34a' } }[p] || { bg: '#fffbeb', text: '#d97706' });

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Projects</h1>
            <p style={styles.subtitle}>{projects.length} total project{projects.length !== 1 ? 's' : ''}</p>
          </div>
          {can('projects', 'add') && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...styles.primaryBtn, background: 'white', color: '#2563eb', border: '1.5px solid #bfdbfe' }} onClick={handleQuickStartClick}>
                🚀 Quick Start
              </button>
              <button style={styles.primaryBtn} onClick={() => setShowForm(true)}>+ New Project</button>
            </div>
          )}
        </div>

        <div style={styles.tabs}>
          {['all', 'active', 'overdue', 'completed', 'not_started'].map(f => (
            <button key={f} style={{ ...styles.tab, ...(filter === f ? styles.tabActive : {}) }} onClick={() => setFilter(f)}>
              {statusLabel(f)}
            </button>
          ))}
        </div>
        {canReorderProjects ? (
          <p style={styles.reorderHint}>⠿ Drag projects and tasks to reorder them</p>
        ) : (
          <p style={styles.reorderHint}>Switch to the "All" tab to drag-and-drop reorder projects</p>
        )}

        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>New Project</h2>
            {error && <p style={styles.error}>{error}</p>}
            <ProjectForm
              data={form}
              onChange={setForm}
              departments={departments}
              companies={companies}
              classifying={classifying}
              onDescriptionBlur={() => handleDescriptionBlur(form, setForm)}
              onSubmit={handleSubmit}
              onCancel={() => { setShowForm(false); setForm(emptyForm); }}
              saving={saving}
              isEdit={false}
            />
          </div>
        )}

        {(() => {
          if (loading) return <p style={styles.loading}>Loading projects...</p>;
          if (filtered.length === 0) return <div style={styles.empty}><p style={styles.emptyText}>{filter === 'all' ? 'No projects yet. Create your first one!' : `No ${filter} projects.`}</p></div>;

          const companyGroups = companies.length > 0
            ? companies
                .map(c => ({ ...c, projects: filtered.filter(p => String(p.meta?.gpf_company_id) === String(c.id)) }))
                .filter(g => g.projects.length > 0 || companies.length === 1)
            : [{ id: null, name: null, projects: filtered }];
          const unassigned = companies.length > 0
            ? filtered.filter(p => !companies.some(c => String(c.id) === String(p.meta?.gpf_company_id)))
            : [];
          if (unassigned.length > 0) companyGroups.push({ id: 'unassigned', name: 'Other Projects', projects: unassigned });
          return companyGroups.map(group => {
            const isCompanyOpen = !group.name || group.id === 'unassigned' || expandedCompanies.has(group.id);
            return (
            <div key={group.id || 'flat'} style={{ marginBottom: 28 }}>
              {group.name && companies.length > 0 && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}
                  onClick={() => toggleCompanyExpanded(group.id)}
                >
                  <button style={styles.expandArrow} title={isCompanyOpen ? 'Collapse' : 'Expand to see projects'}>
                    {isCompanyOpen ? '▾' : '▸'}
                  </button>
                  <span style={{ fontSize: 17 }}>🏢</span>
                  <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: '#0f172a' }}>{group.name}</h2>
                  <span style={styles.taskCountBadge}>
                    ✓ {group.projects.length} project{group.projects.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {isCompanyOpen && (
          <div style={styles.projectList}>
            <div style={styles.tableHeader}>
              <span style={{ flex: 3 }}>Project</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Status</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Priority</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Progress</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Due Date</span>
              <span style={{ flex: 1 }}>Assigned</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
            </div>

            {group.projects.map(project => {
              const status    = project.meta?.gpf_status     || 'active';
              const priority  = project.meta?.gpf_priority   || 'medium';
              const progress  = project.meta?.gpf_progress   || 0;
              const endDate   = project.meta?.gpf_end_date   || '';
              const startDate = project.meta?.gpf_start_date || '';
              const isOverdue = endDate && endDate < today && status !== 'completed';
              const sc        = statusStyle(status);
              const pc        = priorityStyle(priority);
              const health    = getScheduleHealth(startDate, endDate, progress, status);
              const urgentClass = health === HEALTH.RED ? 'gpf-urgent-overdue' : (isDueSoon(endDate, status) ? 'gpf-urgent-duesoon' : '');
              return (
                <div
                  key={`project-${project.id}`}
                  className={urgentClass}
                  onDragOver={(e) => canReorderProjects && e.preventDefault()}
                  onDrop={() => canReorderProjects && handleProjectDrop(project.id)}
                  style={{ opacity: dragProjectId === project.id ? 0.4 : 1 }}
                >
                  <div style={{ ...styles.projectRow, borderLeft: `4px solid ${health.border}`, opacity: status === 'completed' ? 0.6 : 1, background: (() => {
                    if (status === 'completed') return '#f8fafc';
                    switch(health.border) {
                      case '#dc2626': return '#fff5f5';
                      case '#d97706': return '#fffbf0';
                      case '#16a34a': return '#f0fdf4';
                      default:        return '#fff';
                    }
                  })() }}>
                    <div style={{ flex: 3 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {canReorderProjects && (
                          <span
                            draggable
                            onDragStart={() => setDragProjectId(project.id)}
                            onDragEnd={() => setDragProjectId(null)}
                            style={styles.dragHandle}
                            title="Drag to reorder"
                          >⠿</span>
                        )}
                        {taskMap[project.id] !== undefined && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(project.id); }}
                            style={styles.expandArrow}
                            title={expandedProjects.has(project.id) ? 'Collapse tasks' : 'Expand tasks'}
                          >
                            {expandedProjects.has(project.id) ? '▾' : '▸'}
                          </button>
                        )}
                        <span style={{ ...styles.projectName, textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer' }} onClick={() => setDetailProject(project)}>
                          {decodeEntities(project.title?.rendered) || 'Untitled'}
                        </span>
                        {taskMap[project.id] !== undefined && (
                          <span style={styles.taskCountBadge} onClick={(e) => { e.stopPropagation(); toggleExpanded(project.id); }} title="Click to view tasks">
                            ✓ {taskMap[project.id].length} task{taskMap[project.id].length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {project.meta?.gpf_department && (
                        <span style={styles.deptBadge}>{project.meta.gpf_department}</span>
                      )}
                      {project.meta?.gpf_started_by && (
                        <span style={styles.startedBy}>🚀 Created by {project.meta.gpf_started_by}{project.meta?.gpf_started_date && ` on ${project.meta.gpf_started_date}`}</span>
                      )}
                      {attachCounts[project.id] > 0 && (
                        <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#fef3c7', color: '#d97706', marginLeft: 4 }}>
                          📎 {attachCounts[project.id]}
                        </span>
                      )}
                      <div style={styles.progressBar}>
                        <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                      </div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <span style={{ ...styles.badge, background: sc.bg, color: sc.text }}>{status}</span>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <span style={{ ...styles.badge, background: pc.bg, color: pc.text }}>{priority}</span>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{progress}%</div>
                    <div style={{ flex: 1, textAlign: 'center', fontSize: 13, color: isOverdue ? '#ef4444' : '#64748b', fontWeight: isOverdue ? 600 : 400 }}>{endDate || '—'}</div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                      {(() => {
                        const assignedCount = JSON.parse(project.meta?.gpf_assigned_users || '[]').length;
                        return assignedCount > 0 ? (
                          <span style={styles.assignedBadge}>
                            👤 {assignedCount} assigned
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#cbd5e1' }}>Unassigned</span>
                        );
                      })()}
                    </div>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      <button style={{ ...styles.iconBtn, color: '#7c3aed' }} onClick={() => navigate(`/reports?tab=activity&project_id=${project.id}`)} title="View Activity Timeline">🕐</button>
                      <button style={{ ...styles.iconBtn, color: '#2563eb' }} onClick={() => handleEdit(project)} title="Edit">✎</button>
                      <button style={{ ...styles.iconBtn, color: '#2563eb' }} onClick={() => setNotesProject(project)} title="Project Messages">💬</button>
                      <button style={{ ...styles.iconBtn, color: '#16a34a' }} onClick={() => handleDuplicate(project)} title="Duplicate">⧉</button>
                      <button style={{ ...styles.iconBtn, color: '#ef4444' }} onClick={() => handleDelete(project.id, decodeEntities(project.title?.rendered))} title="Delete">✕</button>
                    </div>
                  </div>

                  {taskMap[project.id] !== undefined && expandedProjects.has(project.id) && (
                    <div style={styles.inlineTasks}>
                      {taskMap[project.id].length > 0 && (
                        <div style={styles.inlineTasksHeader}>
                          TASKS ({taskMap[project.id].length})
                        </div>
                      )}
                      {taskMap[project.id].length === 0 && (
                        <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 8px' }}>No tasks yet.</p>
                      )}
                      {taskMap[project.id].map(task => {
                        const tStatus    = task.meta?.gpf_status  || 'not_started';
                        const tProgress  = parseInt(task.meta?.gpf_progress || 0);
                        const tPriority  = task.meta?.gpf_priority || 'medium';
                        const isDone     = tStatus === 'completed' || tProgress === 100;
                        const tpc        = priorityStyle(tPriority);
                        const tHealth    = getScheduleHealth(task.meta?.gpf_start_date || '', task.meta?.gpf_end_date || '', tProgress, tStatus);
                        const tUrgentClass = !isDone && (tHealth === HEALTH.RED ? 'gpf-urgent-overdue' : (isDueSoon(task.meta?.gpf_end_date || '', tStatus) ? 'gpf-urgent-duesoon' : ''));
                        let attachCount = 0;
                        try { attachCount = JSON.parse(task.meta?.gpf_file_ids || '[]').length; } catch {}
                        return (
                          <div
                            key={`task-${project.id}-${task.id}`}
                            className={tUrgentClass}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={(e) => { e.stopPropagation(); handleTaskDrop(project.id, task.id); }}
                            style={{
                              ...styles.inlineTaskRow,
                              borderLeft: `4px solid ${tHealth.border}`,
                              opacity: dragTaskInfo?.taskId === task.id ? 0.4 : 1,
                              background: isDone ? '#f8fafc' : (() => {
                                switch (tHealth.border) {
                                  case '#dc2626': return '#fff5f5';
                                  case '#d97706': return '#fffbf0';
                                  case '#16a34a': return '#f0fdf4';
                                  default:        return '#f8fafc';
                                }
                              })(),
                            }}
                            onClick={() => setDetailTask(task)}
                          >
                            {/* Drag handle */}
                            <span
                              draggable
                              onDragStart={(e) => { e.stopPropagation(); setDragTaskInfo({ projectId: project.id, taskId: task.id }); }}
                              onDragEnd={(e) => { e.stopPropagation(); setDragTaskInfo(null); }}
                              style={styles.dragHandle}
                              title="Drag to reorder"
                              onClick={e => e.stopPropagation()}
                            >⠿</span>
                            {/* Checkbox */}
                            <div style={{ ...styles.taskCheckbox, borderColor: isDone ? '#16a34a' : '#cbd5e1', background: isDone ? '#16a34a' : 'white', flexShrink: 0 }}>
                              {isDone && <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>✓</span>}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 14, fontWeight: 500, color: isDone ? '#94a3b8' : '#0f172a', textDecoration: isDone ? 'line-through' : 'none' }}>
                                  {decodeEntities(task.title?.rendered) || 'Untitled'}
                                </span>
                                {attachCount > 0 && (
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 99, background: '#fef3c7', color: '#d97706' }}>
                                    📎 {attachCount}
                                  </span>
                                )}
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99, background: tpc.bg, color: tpc.text }}>
                                  {tPriority.charAt(0).toUpperCase() + tPriority.slice(1)}
                                </span>
                                {isDone && (
                                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a' }}>Done</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                <div style={{ width: 90, height: 5, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${tProgress}%`, background: tHealth.border, borderRadius: 99 }} />
                                </div>
                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{tProgress}%</span>
                                {task.meta?.gpf_end_date && (
                                  <span style={{ fontSize: 12, color: tHealth.border, fontWeight: 500 }}>Due {task.meta.gpf_end_date}</span>
                                )}
                              </div>
                            </div>

                            {/* Icon actions — same format as the Tasks page */}
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                              <button style={styles.taskIconBtn} onClick={() => setDetailTask(task)} title="Notes">📝</button>
                              {can('projects', 'edit') && (
                                <button style={{ ...styles.taskIconBtn, color: '#2563eb' }} onClick={() => navigate(`/projects/${project.id}/tasks?task=${task.id}&edit=1`)} title="Edit">✎</button>
                              )}
                              {can('projects', 'add') && (
                                <button style={{ ...styles.taskIconBtn, color: '#16a34a' }} onClick={() => navigate(`/projects/${project.id}/tasks?task=${task.id}`)} title="Open in Tasks">⧉</button>
                              )}
                              {can('projects', 'delete') && (
                                <button style={{ ...styles.taskIconBtn, color: '#ef4444' }} onClick={() => handleDeleteTask(task.id, decodeEntities(task.title?.rendered), project.id)} title="Delete">✕</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <button
                        style={styles.inlineAddBtn}
                        onClick={e => { e.stopPropagation(); navigate(`/projects/${project.id}/tasks?new=1`); }}
                      >
                        □ Add task
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
              )}
            </div>
          );
          });
        })()}
      </div>

      {/* Task Detail Modal */}
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={(t) => { setDetailTask(null); const pid = t.meta?.gpf_project_id; if (pid) navigate(`/projects/${pid}/tasks?task=${t.id}&edit=1`); }}
          onOpenNotes={(t) => { setDetailTask(null); const pid = t.meta?.gpf_project_id; if (pid) navigate(`/projects/${pid}/tasks?task=${t.id}`); }}
        />
      )}

      {/* Project Detail Modal */}
      {detailProject && (
        <ProjectDetailModal
          project={detailProject}
          tasks={taskMap[detailProject.id] || []}
          onClose={() => setDetailProject(null)}
          onEdit={handleEdit}
          onOpenNotes={(p) => setNotesProject(p)}
        />
      )}

      {/* Plan Limit Upgrade Modal */}
      {showUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 36, maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🚀</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>You've hit your plan limit</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>{upgradeMsg}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ display: 'block', padding: '12px 24px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%' }}
                onClick={() => { setShowUpgrade(false); navigate('/workspace?upgrade=1'); }}
              >
                Upgrade Now →
              </button>
              <button
                style={{ padding: '11px 24px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
                onClick={() => setShowUpgrade(false)}
              >
                Maybe Later
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 16 }}>
              Questions? Email <a href="mailto:billing@goprojectflow.com" style={{ color: '#2563eb' }}>billing@goprojectflow.com</a>
            </p>
          </div>
        </div>
      )}

      {/* AI Agent Upgrade Modal */}
      {showAiUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 36, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}><div style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, #7c3aed, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><IconAIAgent width={30} height={30} /></div></div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>AI Agent Not Available</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
              Quick Start uses our AI Agent to build your project plan. This feature is included in the Business plan.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ padding: '12px 24px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                onClick={() => { setShowAiUpgrade(false); navigate('/workspace?upgrade=1'); }}
              >Upgrade Plan →</button>
              <button
                style={{ padding: '11px 24px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
                onClick={() => setShowAiUpgrade(false)}
              >Maybe Later</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Start Wizard */}
      {showQuickStart && (
        <QuickStartWizard
          onClose={() => setShowQuickStart(false)}
          onCreated={() => fetchProjects()}
        />
      )}

      {/* Project Notes Panel */}
      {notesProject && (
        <ProjectNotesPanel
          project={notesProject}
          onClose={() => setNotesProject(null)}
        />
      )}

      {/* Color legend — click to filter by schedule health */}
      <div style={styles.legend}>
        {Object.values(HEALTH).map(({ color, bg, label }) => {
          const active = healthFilter === label;
          return (
            <button
              key={label}
              style={{
                ...styles.legendItem,
                background: active ? bg : 'transparent',
                border: active ? `1.5px solid ${color}` : '1.5px solid transparent',
              }}
              onClick={() => setHealthFilter(active ? null : label)}
              title={active ? 'Click to clear filter' : `Show only "${label}" items`}
            >
              <div style={{ width: 14, height: 14, borderRadius: 3, background: bg, border: `1.5px solid ${color}`, flexShrink: 0 }} />
              <span style={{ ...styles.legendLabel, color: active ? color : styles.legendLabel.color, fontWeight: active ? 700 : 500 }}>{label}</span>
            </button>
          );
        })}
        {healthFilter && (
          <button style={styles.legendClear} onClick={() => setHealthFilter(null)}>✕ Clear</button>
        )}
      </div>

      {editProject && (
        <div style={styles.modalOverlay} onClick={() => setEditProject(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.formTitle}>Edit Project</h2>
              <button style={styles.closeModalBtn} onClick={() => setEditProject(null)}>✕</button>
            </div>
            <ProjectForm
              data={editForm}
              onChange={setEditForm}
              departments={departments}
              companies={companies}
              classifying={classifying}
              onDescriptionBlur={() => handleDescriptionBlur(editForm, setEditForm)}
              onSubmit={handleUpdate}
              onCancel={() => setEditProject(null)}
              saving={saving}
              isEdit={true}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  page:           { maxWidth: 1100 },
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:          { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:       { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  primaryBtn:     { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  tabs:           { display: 'flex', gap: 4, marginBottom: 24, background: '#fff', padding: 4, borderRadius: 8, width: 'fit-content', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tab:            { padding: '6px 16px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: '#64748b' },
  tabActive:      { background: '#2563eb', color: '#fff' },
  formCard:       { background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formTitle:      { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  form:           { display: 'flex', flexDirection: 'column', gap: 16 },
  formGrid:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  field:          { display: 'flex', flexDirection: 'column', gap: 6 },
  label:          { fontSize: 13, fontWeight: 600, color: '#374151' },
  input:          { padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  error:          { color: '#ef4444', fontSize: 13, margin: '0 0 12px' },
  formActions:    { display: 'flex', justifyContent: 'flex-end', gap: 12 },
  cancelBtn:      { padding: '9px 18px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  loading:        { color: '#64748b', fontSize: 14 },
  empty:          { background: '#fff', borderRadius: 10, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyText:      { color: '#94a3b8', fontSize: 14 },
  projectList:    { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tableHeader:    { display: 'flex', alignItems: 'center', padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', gap: 8, flexWrap: 'wrap' },
  projectRow:     { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', gap: 8, boxSizing: 'border-box', flexWrap: 'wrap' },
  projectName:    { fontSize: 14, fontWeight: 600, color: '#0f172a', cursor: 'pointer', display: 'block', marginBottom: 4 },
  deptBadge:      { display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '2px 8px', borderRadius: 99, marginBottom: 4, marginRight: 4 },
  startedBy:      { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 },
  progressBar:    { height: 4, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden', maxWidth: 200 },
  progressFill:   { height: '100%', background: '#2563eb', borderRadius: 99 },
  badge:          { padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', display: 'inline-block' },
  iconBtn:        { width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' },
  miniAvatar:     { width: 24, height: 24, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 },
  assignedBadge:  { fontSize: 12, fontWeight: 600, color: '#374151', background: '#f1f5f9', padding: '4px 10px', borderRadius: 99, whiteSpace: 'nowrap' },
  expandArrow:    { width: 20, height: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 },
  taskCountBadge: { fontSize: 11, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '2px 9px', borderRadius: 99, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  dragHandle:     { cursor: 'grab', color: '#cbd5e1', fontSize: 14, flexShrink: 0, userSelect: 'none' },
  reorderHint:    { fontSize: 12, color: '#94a3b8', margin: '8px 0 0' },
  legend:         { display: 'flex', gap: 10, marginTop: 16, padding: '10px 16px', background: '#fff', borderRadius: 8, width: 'fit-content', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap', alignItems: 'center' },
  legendItem:     { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', background: 'transparent' },
  legendLabel:    { fontSize: 12, color: '#64748b', fontWeight: 500 },
  legendClear:    { fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '5px 8px' },
  inlineTasksHeader: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  inlineTasks:    { background: 'white', borderTop: '1px solid #f1f5f9', padding: '10px 20px 14px 28px', display: 'flex', flexDirection: 'column', gap: 6 },
  inlineLoading:  { fontSize: 13, color: '#94a3b8', margin: 0 },
  inlineEmpty:    { display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: '#94a3b8' },
  taskCheckbox:   { width: 16, height: 16, borderRadius: 3, border: '1.5px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  taskIconBtn:    { width: 26, height: 26, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  inlineAddBtn:   { padding: '6px 14px', background: 'white', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 },
  inlineTaskRow:  { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  inlineTaskName: { flex: 2, fontSize: 13, fontWeight: 500, color: '#0f172a' },
  inlineProgressBar:  { flex: 1, height: 4, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden', maxWidth: 120 },
  inlineProgressFill: { height: '100%', borderRadius: 99 },
  inlineProgressPct:  { fontSize: 12, fontWeight: 600, color: '#64748b', minWidth: 32 },
  manageTasksBtn: { alignSelf: 'flex-end', background: 'none', border: 'none', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginTop: 4 },
  modalOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:          { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  closeModalBtn:  { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' },
};